/**
 * Unified storage utility for consistent token and user data handling.
 * Uses sessionStorage exclusively to fix the Angular inconsistency where
 * localStorage and sessionStorage were used interchangeably.
 */

const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth-token',
  CURRENT_USER: 'currentUser',
  USER_DATA: 'userData',
} as const;

export interface StoredUser {
  userId: number;
  userEmail: string;
  userName: string;
  userRole: string;
  token: string;
}

/**
 * Get the authentication token from session storage
 */
export const getAuthToken = (): string | null => {
  return sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Set the authentication token in session storage
 */
export const setAuthToken = (token: string): void => {
  sessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
};

/**
 * Remove the authentication token from session storage
 */
export const removeAuthToken = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Get the current user from session storage
 */
export const getCurrentUser = (): StoredUser | null => {
  const userStr = sessionStorage.getItem(STORAGE_KEYS.CURRENT_USER);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr) as StoredUser;
  } catch {
    return null;
  }
};

/**
 * Set the current user in session storage
 */
export const setCurrentUser = (user: StoredUser): void => {
  sessionStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
};

/**
 * Remove the current user from session storage
 */
export const removeCurrentUser = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
};

/**
 * Get user data from session storage
 */
export const getUserData = (): StoredUser | null => {
  const userStr = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr) as StoredUser;
  } catch {
    return null;
  }
};

/**
 * Set user data in session storage
 */
export const setUserData = (user: StoredUser): void => {
  sessionStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
};

/**
 * Remove user data from session storage
 */
export const removeUserData = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return token !== null && token.length > 0;
};

/**
 * Clear all authentication data from session storage
 */
export const clearAuth = (): void => {
  removeAuthToken();
  removeCurrentUser();
  removeUserData();
};

/**
 * Store all authentication data after successful login
 */
export const storeAuthData = (token: string, user: StoredUser): void => {
  setAuthToken(token);
  setCurrentUser(user);
  setUserData(user);
};

export default {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getCurrentUser,
  setCurrentUser,
  removeCurrentUser,
  getUserData,
  setUserData,
  removeUserData,
  isAuthenticated,
  clearAuth,
  storeAuthData,
  STORAGE_KEYS,
};
